/**
 * 固定比例裁剪弹窗：用户拖动裁剪框选择区域后确认导出。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PRODUCT_CAROUSEL_ASPECT,
  PRODUCT_CAROUSEL_ASPECT_LABEL,
  computeMaxCenteredCropBox,
  exportCroppedImageFile,
  loadImageElement,
  readFileAsDataUrl,
} from '@/components/image/fixedAspectCrop';
import '@/components/image/FixedAspectCropModal.css';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function displayRectForContain(viewportW, viewportH, naturalW, naturalH) {
  const vw = Math.max(1, viewportW);
  const vh = Math.max(1, viewportH);
  const scale = Math.min(vw / naturalW, vh / naturalH);
  const w = Math.max(1, naturalW * scale);
  const h = Math.max(1, naturalH * scale);
  return {
    left: (vw - w) / 2,
    top: (vh - h) / 2,
    width: w,
    height: h,
    scale,
  };
}

/**
 * @param {{
 *   open: boolean,
 *   file?: File|Blob|null,
 *   src?: string,
 *   fileName?: string,
 *   aspect?: number,
 *   aspectLabel?: string,
 *   title?: string,
 *   hint?: string,
 *   confirming?: boolean,
 *   outputWidth?: number,
 *   outputHeight?: number,
 *   maxEdge?: number,
 *   maxBytes?: number,
 *   onCancel?: () => void,
 *   onConfirm?: (file: File) => void | Promise<void>,
 * }} props
 */
export default function FixedAspectCropModal({
  open = false,
  file = null,
  src = '',
  fileName = '',
  aspect = PRODUCT_CAROUSEL_ASPECT,
  aspectLabel = PRODUCT_CAROUSEL_ASPECT_LABEL,
  title = '裁剪图片',
  hint = '',
  confirming = false,
  outputWidth,
  outputHeight,
  maxEdge,
  maxBytes,
  minCropEdge = 0,
  onCancel,
  onConfirm,
}) {
  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const [objectUrl, setObjectUrl] = useState('');
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [display, setDisplay] = useState(null);
  const [cropBox, setCropBox] = useState(null); // display coords relative to image rect
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');

  const resolvedHint = hint || `平台要求商品图比例为 ${aspectLabel}，请拖动裁剪框选择保留区域`;

  useEffect(() => {
    if (!open) {
      setObjectUrl('');
      setNatural({ width: 0, height: 0 });
      setDisplay(null);
      setCropBox(null);
      setBusy(false);
      setLoadError('');
      return undefined;
    }

    let cancelled = false;
    let createdUrl = '';

    (async () => {
      try {
        let nextSrc = String(src || '').trim();
        if (!nextSrc && file) {
          if (typeof URL !== 'undefined' && file instanceof Blob) {
            createdUrl = URL.createObjectURL(file);
            nextSrc = createdUrl;
          } else {
            nextSrc = await readFileAsDataUrl(file);
          }
        }
        if (!nextSrc) throw new Error('缺少待裁剪图片');
        if (cancelled) return;
        setObjectUrl(nextSrc);
        const img = await loadImageElement(nextSrc);
        if (cancelled) return;
        setNatural({
          width: img.naturalWidth || img.width,
          height: img.naturalHeight || img.height,
        });
        setLoadError('');
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || '图片加载失败');
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, file, src]);

  const layoutCrop = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !(natural.width > 0) || !(natural.height > 0)) return;
    const rect = displayRectForContain(
      viewport.clientWidth,
      viewport.clientHeight,
      natural.width,
      natural.height,
    );
    setDisplay(rect);
    const maxBox = computeMaxCenteredCropBox(natural.width, natural.height, aspect);
    setCropBox({
      left: rect.left + maxBox.sx * rect.scale,
      top: rect.top + maxBox.sy * rect.scale,
      width: maxBox.sw * rect.scale,
      height: maxBox.sh * rect.scale,
    });
  }, [natural.width, natural.height, aspect]);

  useEffect(() => {
    if (!open) return undefined;
    layoutCrop();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', layoutCrop);
      return () => window.removeEventListener('resize', layoutCrop);
    }
    const ro = new ResizeObserver(() => layoutCrop());
    ro.observe(viewport);
    return () => ro.disconnect();
  }, [open, layoutCrop]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy && !confirming) onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, confirming, onCancel]);

  const pixelBox = useMemo(() => {
    if (!display || !cropBox || !(display.scale > 0)) return null;
    const sx = clamp(Math.round((cropBox.left - display.left) / display.scale), 0, natural.width);
    const sy = clamp(Math.round((cropBox.top - display.top) / display.scale), 0, natural.height);
    const sw = clamp(Math.round(cropBox.width / display.scale), 1, natural.width - sx);
    const sh = clamp(Math.round(cropBox.height / display.scale), 1, natural.height - sy);
    return { sx, sy, sw, sh };
  }, [display, cropBox, natural.width, natural.height]);

  const onPointerDown = (e) => {
    if (!cropBox || busy || confirming) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originLeft: cropBox.left,
      originTop: cropBox.top,
    };
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || !display || !cropBox) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const minLeft = display.left;
    const minTop = display.top;
    const maxLeft = display.left + display.width - cropBox.width;
    const maxTop = display.top + display.height - cropBox.height;
    setCropBox((prev) => ({
      ...prev,
      left: clamp(drag.originLeft + dx, minLeft, maxLeft),
      top: clamp(drag.originTop + dy, minTop, maxTop),
    }));
  };

  const onPointerUp = (e) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const handleConfirm = async () => {
    if (!pixelBox || busy || confirming) return;
    if (minCropEdge > 0 && (pixelBox.sw < minCropEdge || pixelBox.sh < minCropEdge)) {
      setLoadError(
        `裁剪区域过小：宽高均须 ≥${minCropEdge}px（当前选区约 ${pixelBox.sw}×${pixelBox.sh}），请换更大原图`,
      );
      return;
    }
    setBusy(true);
    try {
      const nameHint = fileName
        || (file && 'name' in file ? file.name : '')
        || 'product';
      const cropped = await exportCroppedImageFile(objectUrl || imgRef.current, pixelBox, {
        fileName: nameHint,
        outputWidth,
        outputHeight,
        maxEdge,
        maxBytes,
      });
      await onConfirm?.(cropped);
    } catch (err) {
      setLoadError(err?.message || '裁剪失败');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="ipe-aspect-crop" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        className="ipe-aspect-crop__backdrop"
        aria-label="关闭"
        disabled={busy || confirming}
        onClick={() => {
          if (!busy && !confirming) onCancel?.();
        }}
      />
      <div className="ipe-aspect-crop__panel">
        <header className="ipe-aspect-crop__head">
          <div>
            <h3 className="ipe-aspect-crop__title">{title}</h3>
            <p className="ipe-aspect-crop__hint">{resolvedHint}</p>
          </div>
          <button
            type="button"
            className="ipe-aspect-crop__close"
            disabled={busy || confirming}
            onClick={() => onCancel?.()}
            aria-label="关闭"
          >
            ×
          </button>
        </header>

        <div className="ipe-aspect-crop__viewport" ref={viewportRef}>
          {loadError ? (
            <div className="ipe-aspect-crop__error">{loadError}</div>
          ) : objectUrl && display ? (
            <>
              <img
                ref={imgRef}
                src={objectUrl}
                alt=""
                className="ipe-aspect-crop__image"
                draggable={false}
                style={{
                  left: display.left,
                  top: display.top,
                  width: display.width,
                  height: display.height,
                }}
              />
              {cropBox ? (
                <div
                  className="ipe-aspect-crop__box"
                  style={{
                    left: cropBox.left,
                    top: cropBox.top,
                    width: cropBox.width,
                    height: cropBox.height,
                  }}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                >
                  <span className="ipe-aspect-crop__box-label">{aspectLabel}</span>
                </div>
              ) : null}
            </>
          ) : (
            <div className="ipe-aspect-crop__loading">加载图片中…</div>
          )}
        </div>

        <footer className="ipe-aspect-crop__foot">
          <button
            type="button"
            className="ipe-aspect-crop__btn ipe-aspect-crop__btn--ghost"
            disabled={busy || confirming}
            onClick={() => onCancel?.()}
          >
            取消
          </button>
          <button
            type="button"
            className="ipe-aspect-crop__btn ipe-aspect-crop__btn--primary"
            disabled={busy || confirming || !pixelBox || Boolean(loadError)}
            onClick={handleConfirm}
          >
            {busy || confirming ? '处理中…' : '确认裁剪'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
